/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@capacit-com/lara-law-shared-ui";
import CommentThread from "./components/comments/CommentThread";
import CustomQuillEditor from "../../components/CustomQuillEditor";
import useUserStore from "@/stores/user.store";
import useCommentsStore from "@/stores/comments.store";
import useWorkspaceComments from "@/hooks/useWorkspaceComments";
import CommentComponentSkeleton from "./components/comments/CommentComponentSkeleton";
import AppRoutes from "@/constants/routes/AppRoutes";
import UserAvatar from "@/components/images/UserAvatar";
import { logger } from "@/lib/logger";
import { useWorkspaceContext } from "../../providers/WorkspaceContext";

const CommentsTab: React.FC = () => {
  // Hooks
  const { workspace, tenantUsers, isParentWorkspace, isOrphanedInspection } =
    useWorkspaceContext();
  const workspaceId = workspace!.id;
  const { me } = useUserStore();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { comments } = useCommentsStore();
  const { setComments, setExpandedReplies, setTenantUsers, setFilter, filter } =
    useCommentsStore();
  const { useQueryCommentsByFileId, useQueryGeneralWorkspaceComments, create } =
    useWorkspaceComments();

  const location = useLocation();
  const scrollToCommentId =
    location.state?.scrollToCommentId ??
    new URLSearchParams(location.search).get("scrollToComment") ??
    undefined;
  // State
  const [commentInput, setCommentInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Constants
  const fileId = documentId || (filter !== "workspace" ? filter : undefined);
  const files = [
    ...(workspace?.documents || []),
    ...(workspace?.folders?.flatMap((folder) => folder.documents) || []),
  ];

  // Fetch data - users now come from context
  const fetchedComments = fileId
    ? useQueryCommentsByFileId(workspaceId, fileId)
    : useQueryGeneralWorkspaceComments(workspaceId);

  // Handle functions
  const handleSubmitFromEditor = async () => {
    if (!commentInput.trim()) return;

    await handleAddComment(
      commentInput,
      filter !== "workspace" ? filter : undefined
    );
    setCommentInput("");
  };

  const handleAddComment = async (content: string, fileId?: string) => {
    setIsLoading(true);
    try {
      await create.mutateAsync({
        content,
        workspaceId,
        fileId,
      });
    } catch (error) {
      logger.error("Failed to add workspace comment", {
        operation: "addWorkspaceComment",
        error,
        workspaceId,
        documentId: fileId,
      });
    } finally {
      setIsLoading(false);
    }
  };
  const [isInitialized, setIsInitialized] = useState(false);

  useLayoutEffect(() => {
    if (!workspace) return;

    setIsLoading(true);
    const forbidden = isParentWorkspace || isOrphanedInspection;

    if (forbidden) {
      if (isOrphanedInspection) {
        navigate(AppRoutes.workspaceFiles(workspace.id), { replace: true });
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(AppRoutes.workspaces, { replace: true });
      }
    }

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, isParentWorkspace, isOrphanedInspection]);

  useEffect(() => {
    if (fetchedComments.data && fetchedComments.isFetched) {
      setComments(fetchedComments.data);

      if (!isInitialized) {
        const initExpandedReplies: Record<string, boolean> = {};
        fetchedComments.data.forEach((comment) => {
          initExpandedReplies[comment.id] = comment.replies?.length === 1;
          comment.replies?.forEach((reply) => {
            initExpandedReplies[reply.id] = reply.replies?.length === 1;
          });
        });
        setExpandedReplies(initExpandedReplies);
        setIsInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedComments.data, fetchedComments.isFetched]);

  // Set filter from URL parameter or location state
  useEffect(() => {
    if (documentId) {
      setFilter(documentId);
    } else {
      setFilter(location.state?.filter || "workspace");
    }
  }, [documentId, location.state?.filter, setFilter]);

  const isCommentsReady =
    !!fetchedComments.isFetched && isInitialized && comments.length > 0;

  useEffect(() => {
    if (scrollToCommentId && comments.length > 0 && isCommentsReady) {
      const el = document.getElementById(scrollToCommentId);

      // If the element is found, scroll to it and highlight it
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("bg-warning/10");
        setTimeout(() => {
          el.classList.remove("bg-warning/10");
        }, 2000);
      }

      // if not it was probably a reply, so try to find the parent comment and expand it
      else {
        const parentComment = comments.find((comment) =>
          comment.replies?.some((reply) => reply.id === scrollToCommentId)
        );
        if (parentComment) {
          setExpandedReplies({
            ...useCommentsStore.getState().expandedReplies,
            [parentComment.id]: true,
          });
          // Wait a bit for the UI to update and then try to scroll again
          setTimeout(() => {
            const replyEl = document.getElementById(scrollToCommentId);
            if (replyEl) {
              replyEl.scrollIntoView({ behavior: "smooth", block: "center" });
              replyEl.classList.add("bg-warning/10");
              setTimeout(() => {
                replyEl.classList.remove("bg-warning/10");
              }, 2000);
            }
          }, 300); // Adjust timeout as needed based on UI update speed
        }
      }

      const nextSearch = new URLSearchParams(location.search);
      nextSearch.delete("scrollToComment");
      const search = nextSearch.toString();

      navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
        },
        {
          replace: true,
          state: { ...location.state, scrollToCommentId: undefined },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToCommentId, comments, isCommentsReady]);

  useEffect(() => {
    if (tenantUsers && tenantUsers.length > 0) {
      setTenantUsers(tenantUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantUsers]);

  if (fetchedComments.isError)
    return (
      <div>
        Fejl ved indlæsning af kommentarer: {fetchedComments.error?.message}
      </div>
    );

  if (!workspace) return null;
  if (isParentWorkspace || isOrphanedInspection) return null;
  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col flex-1 gap-3 mb-4">
        <div className="flex flex-col space-y-2">
          <div className="flex gap-2 justify-end">
            <div>
              <Select
                value={filter}
                onValueChange={(value) => {
                  if (value === "workspace") {
                    navigate(AppRoutes.workspaceComments(workspaceId));
                  } else {
                    navigate(
                      AppRoutes.workspaceCommentsDocument(workspaceId, value)
                    );
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter kommentarer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="workspace">
                    Generelle kommentarer
                  </SelectItem>
                  {files.length > 0 &&
                    files.map((file) => (
                      <SelectItem key={file.id} value={file.id}>
                        {file.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="space-y-4 flex-1">
          {fetchedComments.isPending ? (
            <CommentComponentSkeleton />
          ) : comments.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <p>Ingen kommentarer endnu.</p>
            </div>
          ) : (
            comments.map((comment) => {
              return <CommentThread key={comment.id} comment={comment} />;
            })
          )}
        </div>
      </div>
      <div className="sticky bottom-3 z-30 w-full mt-auto">
        {/* Blur backdrop that extends around all sides */}
        <div className="absolute -inset-4 backdrop-blur-xs bg-background/20 rounded-xl z-0"></div>

        {/* Form content */}
        <div className="relative z-10">
          <div className="flex gap-2 items-center">
            <UserAvatar
              user={me}
              avatarClassName="size-8 shrink-0 relative mb-auto mt-2"
              imageClassName="rounded-full"
              fallbackClassName="rounded-full"
            />
            <div className="flex-1">
              <CustomQuillEditor
                placeholder="Skriv en kommentar..."
                className="w-full max-h-[250px] overflow-y-auto"
                value={commentInput}
                onChange={setCommentInput}
                onSubmit={handleSubmitFromEditor}
                disabled={isLoading}
                buttonLoadingMessage="Sender..."
                buttonMessage="Send"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentsTab;
